import React, { useState, useEffect, useCallback } from "react";
import { Question } from "../../../LiveMcq/Components/AddLiveQuestion/types";

interface QuestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  questions: Question[];
  alreadySelectedQuestions: string[];
  maxSelection: number;
  onConfirm: (selectedQuestions: string[]) => void;
  subject: string;
  level: string;
  // New pagination props
  totalPages: number;
  currentPage: number;
  totalDocs: number;
  hasNext: boolean;
  hasPrev: boolean;
  onPageChange: (page: number) => void;
  loading: boolean;
  // Add search handler prop
  onSearchChange: (searchTerm: string) => void;
}

const QuestionsModal: React.FC<QuestionsModalProps> = ({
  isOpen,
  onClose,
  questions,
  alreadySelectedQuestions = [],
  maxSelection,
  onConfirm,
  subject,
  level,
  totalPages,
  currentPage,
  totalDocs,
  hasNext,
  hasPrev,
  onPageChange,
  loading,
  onSearchChange,
}) => {
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageInputValue, setPageInputValue] = useState<string>("");
  const [pageInputError, setPageInputError] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      // Always use the alreadySelectedQuestions passed from parent
      // This ensures selections persist across page navigation and search
      console.log(
        "Modal opened, setting selected questions:",
        alreadySelectedQuestions
      );
      setSelectedQuestions([...alreadySelectedQuestions]);
      setSearchTerm("");
      setPageInputValue("");
      setPageInputError("");
    }
  }, [isOpen]);

  // CRITICAL: Update selected questions whenever alreadySelectedQuestions changes
  // This ensures that when parent updates selections (from search/navigation),
  // the modal UI reflects the current state
  useEffect(() => {
    if (isOpen && Array.isArray(alreadySelectedQuestions)) {
      console.log(
        "alreadySelectedQuestions updated:",
        alreadySelectedQuestions
      );
      setSelectedQuestions([...alreadySelectedQuestions]);
    }
  }, [alreadySelectedQuestions, isOpen]);

  // Debounced search function that preserves selections
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      // Pass current selections along with search term
      onSearchChange(term);
    }, 500),
    [onSearchChange]
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm("");
    onSearchChange("");
  };

  const handleQuestionToggle = (questionId: string) => {
    const newSelectedQuestions = selectedQuestions.includes(questionId)
      ? selectedQuestions.filter((id) => id !== questionId)
      : selectedQuestions.length < maxSelection
      ? [...selectedQuestions, questionId]
      : selectedQuestions;

    setSelectedQuestions(newSelectedQuestions);
  };

  const handleSelectAll = () => {
    const currentPageQuestions = questions.map((q) => q._id);
    const currentPageSelected = currentPageQuestions.filter((id) =>
      selectedQuestions.includes(id)
    );

    if (currentPageSelected.length === currentPageQuestions.length) {
      // Deselect all from current page
      setSelectedQuestions((prev) =>
        prev.filter((id) => !currentPageQuestions.includes(id))
      );
    } else {
      // Select all from current page (up to maxSelection limit)
      const availableSlots = maxSelection - selectedQuestions.length;
      const questionsToAdd = currentPageQuestions
        .filter((id) => !selectedQuestions.includes(id))
        .slice(0, availableSlots);

      setSelectedQuestions((prev) => [...prev, ...questionsToAdd]);
    }
  };

  const handleConfirm = () => {
    onConfirm(selectedQuestions);
    onClose();
  };

  const handlePrevPage = () => {
    if (hasPrev) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNext) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageJump = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
      setPageInputValue("");
      setPageInputError("");
    }
  };

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPageInputValue(value);
    setPageInputError("");
  };

  const handlePageInputSubmit = () => {
    const pageNum = parseInt(pageInputValue.trim());

    if (isNaN(pageNum)) {
      setPageInputError("Please enter a valid page number");
      return;
    }

    if (pageNum < 1) {
      setPageInputError(`Page number must be at least 1`);
      return;
    }

    if (pageNum > totalPages) {
      setPageInputError(`Page number cannot be more than ${totalPages}`);
      return;
    }

    handlePageJump(pageNum);
  };

  const handlePageInputKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      handlePageInputSubmit();
    }
  };

  // Update page input when current page changes externally
  useEffect(() => {
    if (isOpen) {
      setPageInputValue("");
      setPageInputError("");
    }
  }, [currentPage, isOpen]);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-transparent backdrop-blur-md bg-opacity-50 flex items-center justify-center z-50"
      style={{ top: "64px", left: "260px", right: "0", bottom: "0" }}
    >
      <div className="bg-white rounded-lg p-6 w-11/12 max-w-5xl max-h-[calc(100vh-80px)] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-800">
            Select Questions - {subject} ({level})
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm text-gray-600">
              Select up to {maxSelection} questions ({selectedQuestions.length}/
              {maxSelection} selected)
            </p>
            <p className="text-sm text-gray-500">
              Total Questions: {totalDocs}
            </p>
          </div>

          <div className="flex gap-4 mb-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full border rounded px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  type="button"
                >
                  ×
                </button>
              )}
              {loading && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                </div>
              )}
            </div>
            <button
              onClick={handleSelectAll}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-400"
              disabled={questions.length === 0 || loading}
            >
              {questions.filter((q) => selectedQuestions.includes(q._id))
                .length === questions.length
                ? "Deselect Page"
                : "Select Page"}
            </button>
          </div>

          {searchTerm && (
            <div className="text-sm text-gray-600 mb-2">
              {loading ? "Searching..." : `Search results for "${searchTerm}"`}
            </div>
          )}
        </div>

        {/* Questions List */}
        <div className="flex-1 overflow-y-auto mb-4 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">Loading questions...</span>
            </div>
          ) : questions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              {searchTerm
                ? "No questions found matching your search."
                : "No questions available."}
            </p>
          ) : (
            <div className="space-y-3">
              {questions.map((question, index) => (
                <div
                  key={question._id}
                  className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                    selectedQuestions.includes(question._id)
                      ? "bg-blue-50 border-blue-300"
                      : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                  }`}
                  onClick={() => handleQuestionToggle(question._id)}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedQuestions.includes(question._id)}
                      onChange={() => handleQuestionToggle(question._id)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      disabled={
                        !selectedQuestions.includes(question._id) &&
                        selectedQuestions.length >= maxSelection
                      }
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-800 mb-1">
                        Q{(currentPage - 1) * 20 + index + 1}:{" "}
                        {question.questionText}
                      </p>
                      {question.description && (
                        <p className="text-sm text-gray-600 mb-2">
                          {question.description}
                        </p>
                      )}
                      <div className="text-xs text-gray-500 mb-2">
                        Level: {question.level} | Language:{" "}
                        {question.languageName} | Course: {question.courseName}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {question.options?.map((option, optIndex) => (
                          <div
                            key={option._id}
                            className={`text-sm p-2 rounded ${
                              option.isCorrect
                                ? "bg-green-100 text-green-800 border border-green-300"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {String.fromCharCode(65 + optIndex)}.{" "}
                            {option.optionText}
                            {option.isCorrect && (
                              <span className="ml-1 text-green-600 font-bold">
                                ✓
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between py-3 border-t">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Go to page:</span>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={pageInputValue}
                  onChange={handlePageInputChange}
                  onKeyPress={handlePageInputKeyPress}
                  onBlur={handlePageInputSubmit}
                  placeholder={String(currentPage)}
                  className={`w-16 px-2 py-1 text-sm text-center border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    pageInputError ? "border-red-500" : "border-gray-300"
                  }`}
                  disabled={loading}
                />
                {pageInputError && (
                  <span className="text-xs text-red-500">{pageInputError}</span>
                )}
              </div>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages} ({totalPages} pages)
              </span>
              <span className="text-xs text-gray-500">
                ({totalDocs} questions total)
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevPage}
                disabled={!hasPrev || loading}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex gap-1">
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageJump(pageNum)}
                    disabled={loading}
                    className={`px-2 py-1 text-sm rounded ${
                      pageNum === currentPage
                        ? "bg-blue-500 text-white"
                        : "border hover:bg-gray-50"
                    } disabled:opacity-50`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                onClick={handleNextPage}
                disabled={!hasNext || loading}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Done ({selectedQuestions.length} selected)
          </button>
        </div>
      </div>
    </div>
  );
};

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export default QuestionsModal;
